import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { yupResolver } from '@hookform/resolvers/yup';
import { debounce, isString } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Box } from '@mui/system';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import {
  useCreatePipelineMutation,
  useGetPipelineQuery,
  useUpdatePipelineMutation
} from 'src/services/deal-flow/deal-flow.service';
import { CreateDealStageDto, CreatePipelineDto, PipelineStageType, UpdatePipelineDto } from 'src/services/deal-flow/deal-flow.types';
import { getCreatePipelineErrorMessage, getUpdatePipelineErrorMessage } from 'src/services/deal-flow/deal-flow.utils';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { useGetDepartmentsQuery } from 'src/services/organisation/organisation.service';
import { Department } from 'src/services/organisation/organisation.types';
import { useGetCurrenciesQuery } from 'src/services/utils/utils.service';

import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';
import FormProvider, { RHFAutocomplete, RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

// ----------------------------------------------------------------------

interface FormValuesProps {
  name: string;
  currency: string;
  auto_archive_enabled: boolean;
  auto_archive_in_days?: number;
  stages: CreateDealStageDto[];
  departments: { id: string; name: string }[];
}

interface Props {
  open: boolean;
  onClose: VoidFunction;
  pipelineId?: string | null;
}

const DEFAULT_STAGE_COLORS = [
  '#1976d2', // Blue
  '#ed6c02', // Orange  
  '#2e7d32', // Green
  '#9c27b0', // Purple
  '#d32f2f', // Red
  '#0288d1', // Light Blue
  '#f57c00', // Amber
  '#388e3c', // Light Green
];

export default function PipelineEditorModal({ open, onClose, pipelineId }: Props) {

  const { t } = useTranslate();
  const theme = useTheme();
  const authContext = useAuthContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { canAccess } = useFeatures();

  const [departmentSearchQuery, setDepartmentSearchQuery] = useState('');

  const isEdit = !!pipelineId;

  const { data: currencies } = useGetCurrenciesQuery();

  const { data: currentPipeline, isLoading: isLoadingPipeline } = useGetPipelineQuery(
    { pipelineId: pipelineId as string },
    { skip: !pipelineId }
  );

  const { currentData: departments, isLoading: isDepartmentsLoading } = useGetDepartmentsQuery(
    {
      page: {
        page: 1,
        per_page: 999,
      },
      search_query: departmentSearchQuery,
      org_id: authContext?.organization?.id,
    },
    {
      skip: !authContext?.organization?.id,
    }
  );

  const [createPipeline, { isLoading: isCreating }] = useCreatePipelineMutation();
  const [updatePipeline, { isLoading: isUpdating }] = useUpdatePipelineMutation();

  const isSubmitting = isCreating || isUpdating;

  const PipelineSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.required')),
    currency: Yup.string().required(t('validation.required')),
    departments: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().required(t('validation.required')),
        name: Yup.string().required(t('validation.required')),
      })
    ),
    auto_archive_enabled: Yup.boolean(),
    auto_archive_in_days: Yup.number().when('auto_archive_enabled', {
      is: true,
      then: () => Yup.number().required(t('validation.required')).min(1, t('validation.min_integer', { min: 1 })).max(365, t('validation.max_integer', { max: 365 }))
    }),
    stages: Yup.array().of(
      Yup.object().shape({
        identifier: Yup.string().optional(),
        name: Yup.string()
          .required(t('validation.required')),
        order: Yup.number().required(t('validation.required')).min(1),
        type: Yup.string()
          .oneOf(Object.values(PipelineStageType))
          .required(t('validation.required')),
        completion_rate: Yup.number()
          .required(t('validation.required'))
          .min(0, t('validation.min_integer', { min: 0 }))
          .max(100, t('validation.max_integer', { max: 100 })),
        color: Yup.string().optional(),
      })
    ).test(
      'unique-stage-names',
      t('admin-organization.settings.deal_flow_pipeline.validation.unique_stage_name'),
      (stages) => {
        const names = (stages || []).map(stage => stage.name);
        const uniqueNames = new Set(names);
        return uniqueNames.size === names.length; // true if all names are unique
      }
    )
      .min(1, t('admin-organization.settings.deal_flow_pipeline.validation.min_stages'))
      .max(10, t('admin-organization.settings.deal_flow_pipeline.validation.max_stages'))
    ,
  });

  const defaultValues = useMemo(() => ({
    name: currentPipeline?.name || '',
    currency: currentPipeline?.currency?.code || 'USD',
    auto_archive_enabled: currentPipeline?.auto_archive_enabled || false,
    auto_archive_in_days: currentPipeline?.auto_archive_in_days || undefined,
    stages: currentPipeline?.stages
      ?.map(stage => ({
        identifier: stage.id || '',
        name: stage.name,
        order: stage.order,
        type: stage.type,
        completion_rate: stage.completion_rate,
        color: stage.color || '',
      }))
      // Sort stages by order
      ?.sort((a, b) => a.order - b.order) || [
        { name: 'Lead', order: 1, type: PipelineStageType.OPEN, completion_rate: 10, color: DEFAULT_STAGE_COLORS[0] },
      ],
    departments: currentPipeline?.departments || [],
  }), [currentPipeline]);

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(PipelineSchema) as any,
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = methods;

  const autoArchiveEnabled = watch('auto_archive_enabled');

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'stages',
  });

  // if a stage has stage type WON or LOST, set the respective completion rate to 100 or 0
  useEffect(() => {
    fields.forEach((stage, index) => {
      if (stage.type === PipelineStageType.WON) {
        methods.setValue(`stages.${index}.completion_rate`, 100);
      } else if (stage.type === PipelineStageType.LOST) {
        methods.setValue(`stages.${index}.completion_rate`, 0);
      }
    });
  }, [fields, methods]);

  useEffect(() => {
    if (open) {
      if (pipelineId && currentPipeline) {
        reset(defaultValues);
      }
      else {
        reset({
          name: '',
          currency: 'USD',
          stages: [
            { name: 'Lead', order: 1, type: PipelineStageType.OPEN, completion_rate: 10, color: DEFAULT_STAGE_COLORS[0] },
          ],
        });
      }
    }
  }, [open, currentPipeline, pipelineId, reset, defaultValues]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = async (data: FormValuesProps) => {
    try {

      if (isEdit && currentPipeline) {
        const updateData: UpdatePipelineDto = {
          name: data.name,
          currency: data.currency,
          auto_archive_settings: {
            enabled: data.auto_archive_enabled,
            archive_in_days: data.auto_archive_enabled ? data.auto_archive_in_days : undefined,
          },
          stages: data.stages.map(stage => ({
            id: stage.identifier || undefined,
            name: stage.name,
            type: stage.type || PipelineStageType.OPEN,
            order: stage.order,
            completion_rate: stage.completion_rate,
            color: stage.color || DEFAULT_STAGE_COLORS[0],
          })),
          departments: data.departments.map(department => department.id),
        };

        await updatePipeline({
          pipelineId: currentPipeline.id,
          data: updateData,
        }).unwrap();

        enqueueSnackbar(t('admin-organization.settings.deal_flow_pipeline.api.update.success'), {
          variant: 'success'
        });

      } else {
        const createData: CreatePipelineDto = {
          name: data.name,
          auto_archive_settings: {
            enabled: data.auto_archive_enabled || false,
            archive_in_days: data.auto_archive_enabled ? data.auto_archive_in_days : undefined,
          },
          currency: data.currency,
          stages: data.stages
        };

        await createPipeline(createData).unwrap();

        enqueueSnackbar(t('admin-organization.settings.deal_flow_pipeline.api.create.success'), {
          variant: 'success'
        });
      }

      handleClose();
    } catch (error) {

      let errMsg = '';
      if (isEdit) {
        errMsg = getUpdatePipelineErrorMessage(error);
      }
      else {
        errMsg = getCreatePipelineErrorMessage(error);
      }

      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  };

  const handleAddStage = () => {
    const newOrder = fields.length + 1;
    const colorIndex = fields.length % DEFAULT_STAGE_COLORS.length;

    append({
      identifier: '',
      name: '',
      order: newOrder,
      type: PipelineStageType.OPEN,
      completion_rate: 0,
      color: DEFAULT_STAGE_COLORS[colorIndex],
    });
  };

  const handleRemoveStage = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      // Reorder remaining stages
      const currentStages = watch('stages');
      currentStages.forEach((stage, idx) => {
        if (idx >= index) {
          methods.setValue(`stages.${idx}.order`, idx + 1);
        }
      });
    }
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    // dropped outside the list
    if (!destination) return;

    // dropped in the same position
    if (destination.index === source.index) return;

    // Move the field in the form state
    move(source.index, destination.index);

    const currentStages = watch('stages');
    currentStages.forEach((stage, index) => {
      methods.setValue(`stages.${index}.order`, index + 1);
    });
  };

  // Sort fields by order for rendering
  const sortedFields = useMemo(() => {
    if (!fields.length) return [];

    // Create a copy of fields that we can sort
    return [...fields].sort((a, b) => {
      const aOrder = watch(`stages.${fields.findIndex(f => f.id === a.id)}.order`) || 0;
      const bOrder = watch(`stages.${fields.findIndex(f => f.id === b.id)}.order`) || 0;
      return aOrder - bOrder;
    });
  }, [fields, watch]);

  const onDepartmentSearch = useMemo(
    () => debounce((value: string) => {
      setDepartmentSearchQuery(value);
    }, 500),
    [setDepartmentSearchQuery]
  );

  const canEditSettings = useMemo(() => {
    if (!authContext?.account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role);
  }, [authContext]);


  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={handleClose}
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {isEdit
              ? t('admin-organization.settings.deal_flow_pipeline.edit_pipeline')
              : t('admin-organization.settings.deal_flow_pipeline.create_pipeline')
            }
          </Typography>
          <IconButton onClick={handleClose}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>

        {
          canAccess(FeatureType.DEAL_FLOW_ENABLED) ? (
            <DialogContent sx={{ overflowY: 'auto', maxHeight: '70vh' }} dividers>
              <Stack spacing={3}>
                <Alert severity="info">
                  {t('admin-organization.settings.deal_flow_pipeline.modal_alert')}
                </Alert>

                {/* Pipeline Details */}
                <Stack spacing={2}>
                  <Typography variant="subtitle2">
                    {t('admin-organization.settings.deal_flow_pipeline.pipeline_details')}
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <RHFTextField
                      name="name"
                      type='text'
                      inputProps={{ maxLength: 50 }}
                      label={t('admin-organization.settings.deal_flow_pipeline.labels.name')}
                      placeholder={t('admin-organization.settings.deal_flow_pipeline.placeholders.name')}
                    />

                    <RHFSelect
                      name="currency"
                      label={t('admin-organization.settings.deal_flow_pipeline.labels.currency')}
                      placeholder={t('admin-organization.settings.deal_flow_pipeline.placeholders.currency')}
                    >
                      {currencies?.map((option) => (
                        <MenuItem key={option.id} value={option.code}>
                          {option.code}
                        </MenuItem>
                      ))}
                    </RHFSelect>

                  </Stack>

                  <RHFAutocomplete
                    multiple
                    fullWidth
                    onInputChange={(_, value) => onDepartmentSearch(value)}
                    disableCloseOnSelect
                    name="departments"
                    label={t('admin-organization.settings.deal_flow_pipeline.labels.departments')}
                    placeholder="Start typing to search departments..."
                    options={departments?.results ?? []}
                    filterOptions={(x) => x}
                    helperText={t('admin-organization.settings.deal_flow_pipeline.helper_text.departments')}
                    renderOption={(props: any, option: Department) => (
                      <li {...props}>
                        <Stack direction="column">
                          <Typography variant="body1">
                            {`${option.name}`}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    getOptionLabel={(option: Department | string) =>
                      isString(option) ? option : option.name
                    }
                    isOptionEqualToValue={(option: Department, value: Department) => option.id === value.id}
                    getOptionKey={(option) => (option as Department).id as string}
                  />

                </Stack>


                <Divider />
                {/* Auto Archive Settings */}
                <Stack spacing={2}>
                  <Typography variant="subtitle2">
                    {t('admin-organization.settings.deal_flow_pipeline.auto_archive_settings')}
                  </Typography>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
                    <Stack direction="column" alignItems="start">
                      <Typography variant="subtitle2">
                        {t('admin-organization.settings.deal_flow_pipeline.enable_auto_archive')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {t('admin-organization.settings.deal_flow_pipeline.enable_auto_archive_helper')}
                      </Typography>
                    </Stack>
                    <RHFSwitch name="auto_archive_enabled" />
                  </Stack>

                  {autoArchiveEnabled && (
                    <RHFTextField
                      name="auto_archive_in_days"
                      type='number'
                      label={t('admin-organization.settings.deal_flow_pipeline.auto_archive_days')}
                      inputProps={{ min: 1, max: 365 }}
                      helperText={t('admin-organization.settings.deal_flow_pipeline.auto_archive_days_helper')}
                    />
                  )}
                </Stack>

                <Divider />

                {/* Stages Configuration */}
                <Stack spacing={2} pb={5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack>
                      <Typography variant="subtitle2">
                        {t('admin-organization.settings.deal_flow_pipeline.stage_configuration')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('admin-organization.settings.deal_flow_pipeline.drag_hint')}
                      </Typography>
                    </Stack>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Iconify icon="mingcute:add-line" />}
                      onClick={handleAddStage}
                      disabled={!canEditSettings || isSubmitting}
                    >
                      {t('admin-organization.settings.deal_flow_pipeline.labels.add_stage')}
                    </Button>
                  </Stack>

                  {
                    fields.length === 0 && (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('admin-organization.settings.deal_flow_pipeline.no_stages')}
                        </Typography>
                      </Box>
                    )

                  }

                  {
                    fields.length > 0 && (
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="stages">
                          {(provided) => (
                            <Stack
                              spacing={2}
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {
                                sortedFields.map((field, index) => {
                                  // Find the original index in the fields array
                                  const originalIndex = fields.findIndex(f => f.id === field.id);
                                  return (
                                    <Draggable
                                      key={field.id}
                                      draggableId={field.id}
                                      index={index}
                                    >
                                      {(provided2, snapshot) => (
                                        <Card
                                          variant="outlined"
                                          ref={provided2.innerRef}
                                          {...provided2.draggableProps}
                                          sx={{
                                            ...(snapshot.isDragging && {
                                              boxShadow: (themeN) => themeN.customShadows.z20,
                                            }),
                                          }}
                                        >
                                          <CardContent>
                                            <Stack spacing={2}>
                                              <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                {...provided2.dragHandleProps}
                                              >
                                                <Stack direction="row" spacing={1} alignItems="start" justifyContent="center">
                                                  <Tooltip title={t('admin-organization.settings.deal_flow_pipeline.drag_hint')} arrow>
                                                    <Box sx={{ mt: 0.2 }}>
                                                      <Iconify
                                                        icon="icon-park-outline:drag"
                                                        height={15}
                                                        sx={{
                                                          color: 'text.disabled',
                                                          cursor: 'grab',
                                                          '&:hover': { color: 'text.primary' }
                                                        }}
                                                      />
                                                    </Box>
                                                  </Tooltip>
                                                  <Typography variant="subtitle2" color="text.primary">
                                                    {t('admin-organization.settings.deal_flow_pipeline.stage')} {field.order}
                                                  </Typography>
                                                </Stack>
                                                {(!field.identifier) && (
                                                  <IconButton
                                                    size="small"
                                                    color="error"
                                                    disabled={!canEditSettings || isSubmitting}
                                                    onClick={() => handleRemoveStage(originalIndex)}
                                                  >
                                                    <Iconify icon="mingcute:delete-2-line" />
                                                  </IconButton>
                                                )}
                                              </Stack>

                                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                                <RHFTextField
                                                  name={`stages.${originalIndex}.name`}
                                                  label={t('admin-organization.settings.deal_flow_pipeline.labels.stage_name')}
                                                  placeholder={t('admin-organization.settings.deal_flow_pipeline.placeholders.stage_name')}
                                                  helperText={
                                                    errors.stages?.[originalIndex]?.name?.message
                                                      ? t('admin-organization.settings.deal_flow_pipeline.validation.unique_stage_name')
                                                      : ''
                                                  }
                                                  error={!!errors.stages?.[originalIndex]?.name}
                                                />

                                                <RHFSelect
                                                  name={`stages.${originalIndex}.type`}
                                                  label={t('admin-organization.settings.deal_flow_pipeline.labels.stage_type')}
                                                  placeholder={t('admin-organization.settings.deal_flow_pipeline.placeholders.stage_type')}
                                                  sx={{ maxWidth: { sm: 150 } }}
                                                >
                                                  {Object.values(PipelineStageType).map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                      {t(`enums.pipeline_stage_types.${type}`)}
                                                    </MenuItem>
                                                  ))}
                                                </RHFSelect>

                                                <RHFTextField
                                                  name={`stages.${originalIndex}.completion_rate`}
                                                  label={t('admin-organization.settings.deal_flow_pipeline.labels.completion_rate')}
                                                  type="number"
                                                  disabled={watch(`stages.${originalIndex}.type`) !== PipelineStageType.OPEN}
                                                  inputProps={{ min: 0, max: 100 }}
                                                  sx={{ maxWidth: { sm: 150 } }}
                                                />

                                                <RHFTextField
                                                  name={`stages.${originalIndex}.color`}
                                                  label={t('admin-organization.settings.deal_flow_pipeline.labels.color')}
                                                  type="color"
                                                  sx={{ maxWidth: { sm: 150 } }}
                                                />
                                              </Stack>
                                            </Stack>
                                          </CardContent>
                                        </Card>
                                      )}
                                    </Draggable>
                                  )
                                })}
                              {provided.placeholder}
                            </Stack>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )
                  }

                  {
                    errors.stages && (
                      <Stack justifyContent='center' alignItems="center">
                        <Alert severity="error" sx={{ width: '100%' }}>
                          <Typography variant="body2">
                            {errors.stages.root ? errors.stages.root.message : errors.stages.message}
                          </Typography>
                        </Alert>
                      </Stack>
                    )
                  }

                </Stack>
              </Stack>
            </DialogContent>
          ) : (
            <FeatureLockedListView text={t('billing.subscription_required')} onNavigate={onClose} />
          )
        }

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            {t('common.cancel')}
          </Button>

          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={!canEditSettings || !canAccess(FeatureType.DEAL_FLOW_ENABLED)}
          >
            {isEdit ? t('common.update') : t('common.create')}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}